import type { AuthTokenResponse, MarketplaceUser } from "@/app/types/api";

const ACCESS_TOKEN_KEY = "lumina_mkt_access_token";
const REFRESH_TOKEN_KEY = "lumina_mkt_refresh_token";
const ACCESS_TOKEN_EXPIRES_AT_KEY = "lumina_mkt_access_token_expires_at";
const USER_KEY = "lumina_mkt_user";

export type AuthSession = {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: number;
  user: MarketplaceUser;
};

function parseJwtExpiry(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "="
    );
    const decoded = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof decoded.exp === "number" ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}

function parseStoredExpiry(rawExpiry: string | null): number | null {
  if (!rawExpiry) return null;
  const parsed = Number(rawExpiry);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function createAuthSession(data: AuthTokenResponse): AuthSession {
  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    accessTokenExpiresAt:
      parseJwtExpiry(data.access_token) ?? Date.now() + data.expires_in * 1000,
    user: data.user,
  };
}

export function readAuthSession(): AuthSession | null {
  try {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    const rawExpiry = localStorage.getItem(ACCESS_TOKEN_EXPIRES_AT_KEY);
    const rawUser = localStorage.getItem(USER_KEY);
    if (!accessToken || !refreshToken || !rawUser) return null;
    const user = JSON.parse(rawUser) as MarketplaceUser;
    if (!user?.id || !user?.email) return null;
    const accessTokenExpiresAt =
      parseStoredExpiry(rawExpiry) ?? parseJwtExpiry(accessToken);
    if (!accessTokenExpiresAt) return null;
    return { accessToken, refreshToken, accessTokenExpiresAt, user };
  } catch {
    return null;
  }
}

export function writeAuthSession(session: AuthSession): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, session.accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, session.refreshToken);
  localStorage.setItem(
    ACCESS_TOKEN_EXPIRES_AT_KEY,
    String(Math.trunc(session.accessTokenExpiresAt))
  );
  localStorage.setItem(USER_KEY, JSON.stringify(session.user));
}

export function clearAuthSessionStorage(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(ACCESS_TOKEN_EXPIRES_AT_KEY);
  localStorage.removeItem(USER_KEY);
}

export function emitAuthChanged(): void {
  window.dispatchEvent(new CustomEvent("lumina:auth-changed"));
}
